import React from 'react';
import useGameStore from '../../store/gameStore';
import { Target, Trophy, X, Download, Mail } from 'lucide-react';
import generateDebriefPDF from '../../utils/generateDebriefPDF';
import { bilanMecaniques, vuesToList } from '../../utils/mecaniques';
import { SCORE_FORMULE } from '../../utils/scenario';
import { getCompanyLogo } from '../../data/companyLogos';
import { bilanFranchise } from '../../utils/franchise';
import { bilanGroupe } from '../../utils/groupe';
import { bilanMerchandising } from '../../utils/merchandising';

const fmtEur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;

const buildResultatComplet = (r, mecaVues) => ({
  ...r,
  mecaniques: bilanMecaniques(vuesToList(mecaVues)),
  franchise: bilanFranchise(useGameStore.getState().retail),
  groupe: bilanGroupe(useGameStore.getState()),
  merchandising: bilanMerchandising(useGameStore.getState().retail, useGameStore.getState().totalDays || 0, useGameStore.getState().month || 1),
});

/** 01/07 — Envoi auto du débrief PDF au formateur (une fois par scénario). */
const envoyerDebriefFormateur = async (sc, company, mecaVues) => {
  const st = useGameStore.getState();
  const setStatus = (status) => useGameStore.setState({
    sessionScenario: { ...useGameStore.getState().sessionScenario, debriefEmail: status },
  });
  if (!st.licenseKey) { setStatus('none'); return; }
  setStatus('sending');
  try {
    const pdfBase64 = await generateDebriefPDF({
      companyName: company?.name,
      scenarioNom: sc.nom,
      logoSrc: company?.customLogo || getCompanyLogo(company?.logo)?.src || null,
      resultat: buildResultatComplet(sc.resultat || {}, mecaVues),
      output: 'base64',
    });
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/licenses/debrief-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        license_key: st.licenseKey,
        pdf_base64: pdfBase64,
        company_name: company?.name || '',
        scenario_nom: sc.nom || '',
        score: sc.resultat?.score ?? null,
      }),
    });
    const data = await res.json().catch(() => ({}));
    setStatus(data?.ok ? 'sent' : 'none');
  } catch {
    setStatus('error');
  }
};

/**
 * SCD Lot 5 — Bandeau de scénario (Dashboard) + écran de fin de scénario.
 * Affiché uniquement quand un scénario de session formateur est actif.
 */
export default function ScenarioBanner() {
  const sc = useGameStore(s => s.sessionScenario);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const ackScenarioEnd = useGameStore(s => s.ackScenarioEnd);
  const company = useGameStore(s => s.company);
  const mecaVues = useGameStore(s => s.retail?.mecaniques?.vues);

  // 01/07 — déclenche l'envoi auto du débrief au formateur à la fin du scénario.
  React.useEffect(() => {
    if (sc?.ended && sc.debriefEmail === undefined) {
      envoyerDebriefFormateur(sc, company, mecaVues);
    }
  }, [sc?.ended, sc?.debriefEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sc) return null;

  const elapsed = totalDays - (sc.startDay || 0);
  const restants = Math.max(0, sc.dureeJours - elapsed);

  if (sc.ended && !sc.ackEnd) {
    const r = sc.resultat || {};
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="scenario-end-modal">
        <div className="surface-card bg-card border-purple-500/40 max-w-lg w-full p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20 text-purple-300"><Trophy className="w-6 h-6" /></div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Fin du scénario</h2>
              <p className="text-sm text-slate-400">{sc.nom} · {sc.dureeJours} jours</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg bg-ink-800/60 p-3"><p className="text-[10px] uppercase text-slate-500">Trésorerie finale</p><p className="font-mono font-bold" data-testid="scenario-res-treasury">{fmtEur(r.treasury)}</p></div>
            <div className="rounded-lg bg-ink-800/60 p-3"><p className="text-[10px] uppercase text-slate-500">CA cumulé</p><p className="font-mono font-bold">{fmtEur(r.caCumul)}</p></div>
            <div className="rounded-lg bg-ink-800/60 p-3"><p className="text-[10px] uppercase text-slate-500">Satisfaction</p><p className="font-mono font-bold">{r.satisfaction}/100 · {r.noteAvis ?? '—'}★</p></div>
            <div className="rounded-lg bg-ink-800/60 p-3"><p className="text-[10px] uppercase text-slate-500">Points de vente</p><p className="font-mono font-bold">{r.nbMagasins}</p></div>
          </div>
          <div className="rounded-lg border border-purple-500/40 bg-purple-500/10 p-4 text-center">
            <p className="text-[10px] uppercase tracking-wider text-purple-300">Score composite</p>
            <p className="text-3xl font-black text-purple-200" data-testid="scenario-score">{r.score}</p>
            <p className="text-[10px] text-slate-500 mt-1">{SCORE_FORMULE}</p>
          </div>
          {r.debrief && (
            <div className="max-h-56 overflow-y-auto space-y-3 rounded-lg bg-ink-800/60 p-4 text-xs" data-testid="scenario-debrief">
              <p className="font-bold text-slate-200 uppercase tracking-wider text-[10px]">Débrief pédagogique</p>
              {(r.debrief.pointsForts || []).length > 0 && (
                <div>
                  <p className="text-emerald-300 font-bold text-[10px] uppercase mb-1">Points forts</p>
                  <ul className="space-y-1">{r.debrief.pointsForts.map((p, i) => <li key={i} className="text-slate-300">• {p}</li>)}</ul>
                </div>
              )}
              {(r.debrief.aTravailler || []).length > 0 && (
                <div>
                  <p className="text-amber-300 font-bold text-[10px] uppercase mb-1">Points à travailler</p>
                  <ul className="space-y-1">{r.debrief.aTravailler.map((p, i) => <li key={i} className="text-slate-300">• {p}</li>)}</ul>
                </div>
              )}
              {(r.debrief.conseils || []).length > 0 && (
                <div>
                  <p className="text-sky-300 font-bold text-[10px] uppercase mb-1">Conseils pour la suite</p>
                  <ul className="space-y-1">{r.debrief.conseils.map((p, i) => <li key={i} className="text-slate-300">• {p}</li>)}</ul>
                </div>
              )}
            </div>
          )}
          <p className="text-xs text-slate-400">Vos résultats sont transmis automatiquement à votre formateur (classement de la session).</p>
          {sc.debriefEmail === 'sent' && (
            <p className="text-xs font-bold text-emerald-300 flex items-center gap-1.5" data-testid="debrief-email-sent">
              <Mail className="w-3.5 h-3.5" /> Débrief PDF envoyé à votre formateur ✓
            </p>
          )}
          {sc.debriefEmail === 'sending' && (
            <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="debrief-email-sending">
              <Mail className="w-3.5 h-3.5 animate-pulse" /> Envoi du débrief à votre formateur…
            </p>
          )}
          <button
            onClick={() => generateDebriefPDF({ companyName: company?.name, scenarioNom: sc.nom, logoSrc: company?.customLogo || getCompanyLogo(company?.logo)?.src || null, resultat: buildResultatComplet(r, mecaVues) })}
            className="w-full rounded-lg border border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/20 text-purple-200 font-bold py-2.5 transition-colors flex items-center justify-center gap-2"
            data-testid="debrief-pdf-btn"
          >
            <Download className="w-4 h-4" /> Télécharger la fiche PDF de l'équipe
          </button>
          <button
            onClick={ackScenarioEnd}
            className="w-full rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-bold py-2.5 transition-colors"
            data-testid="scenario-continue-btn"
          >
            Continuer la partie librement
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="surface-card border-purple-500/40 bg-purple-500/5 p-4" data-testid="scenario-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-purple-500/20 text-purple-300"><Target className="w-5 h-5" /></div>
          <div>
            <p className="font-bold text-foreground">Scénario : {sc.nom}</p>
            {sc.description && <p className="text-xs text-slate-400 mt-0.5 max-w-xl">{sc.description}</p>}
            <ul className="mt-2 space-y-0.5">
              {(sc.objectifs || []).map((o, i) => (
                <li key={i} className="text-xs text-slate-300 flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-purple-400 inline-block" />{o}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Jours restants</p>
          <p className="text-2xl font-black font-mono text-purple-300" data-testid="scenario-jours-restants">
            {sc.ended ? <X className="w-6 h-6 inline" /> : restants}
          </p>
        </div>
      </div>
    </div>
  );
}
